import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatCurrency } from '../../i18n/i18n';

const STAT_COLORS: Record<string, string> = {
  coding: 'var(--color-stat-coding)', art: 'var(--color-stat-art)',
  sound: 'var(--color-stat-sound)', design: 'var(--color-stat-design)',
  '*': 'var(--color-xp)',
};

/**
 * Equipment shop — buy gear for the office.
 * Also shows active office perks and their effects.
 */
export function openEquipmentDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: 'Equipment & Perks',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:6px;min-width:360px;max-height:500px;overflow-y:auto;';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:11px;min-height:14px;text-align:center;';

  function render() {
    container.innerHTML = '';
    const state = store.getState();
    const theme = store.getThemeData();

    // Active office perks
    if (state.office.perks.length > 0 && theme.entities.perkEffects) {
      const label = document.createElement('div');
      label.textContent = 'OFFICE PERKS';
      label.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;';
      container.appendChild(label);

      for (const perkId of state.office.perks) {
        const effect = theme.entities.perkEffects[perkId];
        if (!effect) continue;
        const el = document.createElement('div');
        el.style.cssText = 'font-size:11px;padding:2px 0;display:flex;justify-content:space-between;';
        el.innerHTML = `<span style="color:var(--color-success);">${effect.name}</span><span style="color:var(--color-text-muted);">${effect.description}</span>`;
        container.appendChild(el);
      }
    }

    // Equipment shop
    if (theme.entities.equipment && theme.entities.equipment.length > 0) {
      const label = document.createElement('div');
      label.textContent = 'BUY EQUIPMENT';
      label.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-top:8px;';
      container.appendChild(label);

      const desc = document.createElement('div');
      desc.textContent = 'Equipment boosts all matching staff for the duration.';
      desc.style.cssText = 'font-size:10px;color:var(--color-text-muted);margin-bottom:4px;';
      container.appendChild(desc);

      for (const equip of theme.entities.equipment) {
        const card = document.createElement('div');
        card.className = 'card';
        card.style.cssText = 'padding:6px 8px;display:flex;justify-content:space-between;align-items:center;gap:8px;';

        const info = document.createElement('div');
        info.style.cssText = 'flex:1;';

        const nameRow = document.createElement('div');
        nameRow.style.cssText = 'display:flex;justify-content:space-between;';
        nameRow.innerHTML = `<strong style="font-size:12px;color:${STAT_COLORS[equip.stat] ?? 'inherit'};">${equip.name}</strong><span style="font-size:11px;font-family:var(--font-pixel);color:var(--color-text-secondary);">${formatCurrency(equip.cost)}</span>`;
        info.appendChild(nameRow);

        const descEl = document.createElement('div');
        descEl.textContent = equip.description;
        descEl.style.cssText = 'font-size:9px;color:var(--color-text-muted);';
        info.appendChild(descEl);

        card.appendChild(info);

        const owned = state.purchasedEquipment?.includes(equip.id);
        const canAfford = !owned && state.company.funds >= equip.cost;
        const btn = document.createElement('button');
        btn.className = owned ? 'btn btn--success' : 'btn btn--primary';
        btn.textContent = owned ? 'Owned' : 'Buy';
        btn.disabled = owned || !canAfford;
        btn.style.cssText = 'font-size:10px;padding:4px 10px;flex-shrink:0;';
        btn.addEventListener('click', () => {
          const r = store.dispatch({ type: 'BUY_EQUIPMENT', equipmentId: equip.id });
          if (r.success) {
            statusEl.textContent = `Bought ${equip.name}!`;
            statusEl.style.color = 'var(--color-success)';
          } else {
            statusEl.textContent = r.error ?? 'Failed';
            statusEl.style.color = 'var(--color-danger)';
          }
          render();
        });
        card.appendChild(btn);

        container.appendChild(card);
      }
    }
  }

  render();
  container.appendChild(statusEl);
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
